import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { fetchProjects } from '@/features/portfolio/api'
import { openTicket } from '@/features/support/api'
import type { ApiError } from '@/lib/api/client'
import type { Priority, Project } from '@/types/domain'
import type { Ticket } from '@/types/support'

const PRIORITIES: Priority[] = ['urgent', 'high', 'normal', 'low']

/**
 * Raising a ticket by hand — the phone call, the corridor conversation, the
 * thing a client mentioned in a meeting.
 *
 * It goes through the same action the inbound mail ingest uses, so it gets a
 * contract, an SLA policy and a clock counted in working minutes on the Oman
 * calendar exactly as an emailed one does. A ticket typed in here is not a
 * lesser record than a ticket that arrived by itself.
 */
export function NewTicketDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [projectId, setProjectId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<Priority>('normal')

  const projects = useQuery<Project[], ApiError>({
    queryKey: ['projects'],
    queryFn: () => fetchProjects(),
    retry: false,
  })

  const raise = useMutation<Ticket, ApiError, void>({
    mutationFn: () =>
      openTicket({
        projectId,
        title: title.trim(),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
        priority,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] })
      void queryClient.invalidateQueries({ queryKey: ['activities'] })
      onClose()
    },
  })

  const ready = projectId !== '' && title.trim().length > 0

  return (
    <Dialog title={t('support.newTicket')} onClose={onClose}>
      <form
        className="flex flex-col gap-3.5 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) raise.mutate()
        }}
      >
        <Field label={t('support.project')}>
          <select
            value={projectId}
            autoFocus
            disabled={raise.isPending}
            onChange={(event) => setProjectId(event.target.value)}
            className={FIELD_CLASS}
          >
            <option value="">{t('support.chooseProject')}</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.key} — {project.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('support.ticketTitle')}>
          <input
            value={title}
            disabled={raise.isPending}
            onChange={(event) => setTitle(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        <Field label={t('support.description')}>
          <textarea
            value={description}
            rows={3}
            disabled={raise.isPending}
            onChange={(event) => setDescription(event.target.value)}
            className={`${FIELD_CLASS} resize-none`}
          />
        </Field>

        <Field label={t('support.priority')}>
          <select
            value={priority}
            disabled={raise.isPending}
            onChange={(event) => setPriority(event.target.value as Priority)}
            className={FIELD_CLASS}
          >
            {PRIORITIES.map((option) => (
              <option key={option} value={option}>
                {t(`priority.${option}`)}
              </option>
            ))}
          </select>
        </Field>

        {raise.isError ? (
          <p className="text-[12px] text-[color:var(--color-state-blocked)]">
            {raise.error.message}
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <p className="text-ink-500 min-w-0 flex-1 text-[11px] leading-tight">
            {t('support.manualNote')}
          </p>
          <button
            type="submit"
            disabled={!ready || raise.isPending}
            className="bg-brand-600 shrink-0 rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
          >
            {raise.isPending ? t('support.raising') : t('support.raise')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
